import type { PaymentFailureCode, PaymentFailureReason } from 'paintscout'

// Stripe `decline_code` and top-level `code` values (https://docs.stripe.com/declines/codes)
const STRIPE_CODES: Record<string, PaymentFailureCode> = {
  insufficient_funds: 'insufficient_funds',
  expired_card: 'expired_card',
  invalid_expiry_month: 'expired_card',
  invalid_expiry_year: 'expired_card',
  incorrect_cvc: 'incorrect_cvc',
  invalid_cvc: 'incorrect_cvc',
  incorrect_number: 'incorrect_number',
  invalid_number: 'incorrect_number',
  incorrect_zip: 'incorrect_address',
  card_not_supported: 'card_not_supported',
  currency_not_supported: 'card_not_supported',
  card_velocity_exceeded: 'limit_exceeded',
  withdrawal_count_limit_exceeded: 'limit_exceeded',
  fraudulent: 'suspected_fraud',
  lost_card: 'suspected_fraud',
  stolen_card: 'suspected_fraud',
  pickup_card: 'suspected_fraud',
  restricted_card: 'suspected_fraud',
  merchant_blacklist: 'suspected_fraud',
  security_violation: 'suspected_fraud',
  card_declined: 'card_declined',
  generic_decline: 'card_declined',
  do_not_honor: 'card_declined',
  call_issuer: 'card_declined',
  transaction_not_allowed: 'card_declined',
  not_permitted: 'card_declined',
  no_action_taken: 'card_declined',
  processing_error: 'processing_error',
  issuer_not_available: 'processing_error',
  try_again_later: 'processing_error'
}

// Payrix `errorCode` values (see apps/api/src/services/webhook/payrix/getErrorDescription.ts) and `txnResults.bankCode`
// values, from Payrix's VAP (Vantiv), VCore and ACH failure code lists (#24597).
// Left `unknown` on purpose, so the contractor sees Payrix's own text: `duplicate_transaction` (the first charge may
// have gone through).
const PAYRIX_CODES: Record<string, PaymentFailureCode> = {
  closed_account: 'account_closed',
  invalid_amount: 'limit_exceeded',
  amount_too_large: 'limit_exceeded',
  account_not_activated: 'card_declined',
  surcharge_limit_exceeded: 'processing_error',
  insufficient_funds: 'insufficient_funds',
  nsf: 'insufficient_funds',
  expired_card: 'expired_card',
  expiration_invalid_for_payment_method: 'expired_card',
  invalid_expiry_year: 'expired_card',
  incorrect_cvv: 'incorrect_cvc',
  invalid_cvv: 'incorrect_cvc',
  invalid_card_number: 'incorrect_number',
  invalid_bin: 'incorrect_number',
  no_such_issuer: 'incorrect_number',
  unable_to_locate_record: 'incorrect_number',
  missing_from_account: 'incorrect_number',
  missing_to_account: 'incorrect_number',
  multiple_accounts_for_type: 'incorrect_number',
  address_match_zip_mismatch: 'incorrect_address',
  zip_match_address_mismatch: 'incorrect_address',
  zip_match_address_not_verified: 'incorrect_address',
  card_not_supported: 'card_not_supported',
  amex_disabled: 'card_not_supported',
  discover_disabled: 'card_not_supported',
  echeck_disabled: 'card_not_supported',
  payment_method_not_allowed: 'card_not_supported',
  payment_method_not_supported: 'card_not_supported',
  daily_limit_exceeded: 'limit_exceeded',
  daily_total_exceeded: 'limit_exceeded',
  monthly_limit_exceeded: 'limit_exceeded',
  monthly_total_exceeded: 'limit_exceeded',
  txn_limit_exceeded: 'limit_exceeded',
  withdrawal_limit_exceeded: 'limit_exceeded',
  withdrawal_count_limit_exceeded: 'limit_exceeded',
  card_velocity_exceeded: 'limit_exceeded',
  ticket_limit_exceeded: 'limit_exceeded',
  pin_try_exceeded: 'limit_exceeded',
  lost_card: 'suspected_fraud',
  stolen_card: 'suspected_fraud',
  pickup_card: 'suspected_fraud',
  restricted_card: 'suspected_fraud',
  security_violation: 'suspected_fraud',
  fraudulent: 'suspected_fraud',
  invalid_account: 'invalid_account',
  invalid_routing: 'invalid_account',
  invalid_primary_account: 'invalid_account',
  account_return_error: 'invalid_account',
  invalid_number: 'invalid_account', // ACH only: 'Invalid Bank Routing Number'
  revocation_of_authorization: 'authorization_revoked',
  declined: 'card_declined',
  do_not_honor: 'card_declined',
  generic_decline: 'card_declined',
  general: 'card_declined',
  call_issuer: 'card_declined',
  transaction_not_allowed: 'card_declined',
  transaction_not_supported: 'card_declined',
  not_permitted: 'card_declined',
  service_not_allowed: 'card_declined',
  illegal_transaction: 'card_declined',
  blocked_by_cardholder: 'card_declined',
  card_already_active: 'card_declined',
  invalid_pin: 'card_declined',
  incorrect_pin: 'card_declined',
  unauthorized: 'card_declined',
  processing_error: 'processing_error',
  connection_error: 'processing_error',
  issuer_not_available: 'processing_error',
  unavailable: 'processing_error',
  platform_error: 'processing_error',
  internal_error: 'processing_error',
  invalid_merchant: 'processing_error',
  invalid_merchant_id: 'processing_error',
  reenter_transaction: 'processing_error',
  pin_key_error: 'processing_error',
  message_format_error: 'processing_error',
  late_reversal: 'processing_error',
  reversal_no_match: 'processing_error'
}

// NACHA ACH return reason codes, and the Canadian PAD codes (R9xx) Payrix sends the same way.
// Left `unknown` on purpose (not a debit failure the contractor can act on): R06, R17, R21, R23, R24, R26, R31, R34,
// R37, R39, R40, R51, R53, R74.
const ACH_RETURN_CODES: Record<string, PaymentFailureCode> = {
  R01: 'insufficient_funds',
  R09: 'insufficient_funds',
  R901: 'insufficient_funds', // NSF (Debit Only)
  R908: 'insufficient_funds', // Funds Not Cleared
  R02: 'account_closed',
  R16: 'account_closed',
  R14: 'account_closed', // Representative payee deceased
  R15: 'account_closed', // Account holder deceased
  R905: 'account_closed',
  R910: 'account_closed', // Payor/Payee Deceased
  R911: 'account_closed', // Account Frozen
  R03: 'invalid_account',
  R04: 'invalid_account',
  R12: 'invalid_account', // Account Sold to Another DFI
  R13: 'invalid_account',
  R20: 'invalid_account',
  R28: 'invalid_account',
  R900: 'invalid_account', // R900-07 Institution ID Invalid, R900-08 Account Number Invalid
  R902: 'invalid_account', // Cannot Trace
  R907: 'invalid_account', // No Debit Allowed
  R912: 'invalid_account',
  R914: 'invalid_account', // Incorrect Payor/Payee Name
  R05: 'authorization_revoked',
  R07: 'authorization_revoked',
  R08: 'authorization_revoked',
  R10: 'authorization_revoked',
  R11: 'authorization_revoked',
  R29: 'authorization_revoked',
  R903: 'authorization_revoked', // Payment Stopped/Recalled
  R915: 'authorization_revoked', // PAD No Agreement Existed
  R916: 'authorization_revoked', // PAD Not According to Agreement
  R917: 'authorization_revoked' // PAD Agreement Revoked
}

export const DEBIT_NOT_ALLOWED_REASON: PaymentFailureReason = { code: 'debit_not_allowed' }
export const CANCELED_BY_USER_REASON: PaymentFailureReason = { code: 'canceled_by_user' }
export const UNKNOWN_FAILURE_REASON: PaymentFailureReason = { code: 'unknown' }

function compact(reason: PaymentFailureReason): PaymentFailureReason {
  const result: PaymentFailureReason = { code: reason.code }
  if (reason.providerCode) result.providerCode = reason.providerCode
  if (reason.message) result.message = reason.message
  return result
}

/** From a Stripe `PaymentIntent.last_payment_error` or a Stripe.js `confirmPayment` error. */
export function fromStripePaymentError(
  error?: { code?: string | null; decline_code?: string | null; message?: string | null } | null
): PaymentFailureReason {
  const providerCode = error?.decline_code || error?.code || undefined
  const code =
    (error?.decline_code && STRIPE_CODES[error.decline_code]) || (error?.code && STRIPE_CODES[error.code]) || 'unknown'
  return compact({ code, providerCode, message: error?.message ?? undefined })
}

/**
 * Payrix processor `originalCode` values where the bankCode is too broad, keyed by bankCode first: the same
 * originalCode means different things on VAP and VCore (127 is a limit under `generic_decline`, an amount under
 * `invalid_amount`). `''` holds the VCore codes that arrive with no bankCode.
 * Left out on purpose, because the list gives them two meanings, so the message decides: VCore 04, 46, 59.
 * VCore 94 is a duplicate (see PAYRIX_CODES).
 */
const PAYRIX_ORIGINAL_CODES: Record<string, Record<string, PaymentFailureCode>> = {
  generic_decline: {
    '112': 'insufficient_funds', // 112–118: "Insufficient Funds. Retry after …"
    '113': 'insufficient_funds',
    '114': 'insufficient_funds',
    '115': 'insufficient_funds',
    '116': 'insufficient_funds',
    '117': 'insufficient_funds',
    '118': 'insufficient_funds',
    '147': 'suspected_fraud', // Fraud/Security
    '317': 'suspected_fraud', // Restricted Card - card under sanction
    '550': 'suspected_fraud', // Advanced Fraud Filter Score Below Threshold
    '555': 'suspected_fraud', // Suspected Fraud
    '127': 'limit_exceeded', // VCore: Daily Card Ticket Limit Exceeded
    '656': 'limit_exceeded', // Exceeds Withdrawal Frequency Limit
    '386': 'processing_error', // Processing Network Error
    '652': 'processing_error', // Decline - Re-try Transaction
    '992': 'processing_error', // Cryptographic failure
    '998': 'processing_error', // Invalid use of MCC
    '042': 'canceled_by_user' // VCore: Customer Cancel
  },
  unauthorized: {
    '375': 'processing_error', // Merchant is not enabled for surcharging
    '377': 'processing_error', // Surcharge is not valid for debit or prepaid cards
    '378': 'processing_error' // Surcharge cannot exceed 4% of the sale amount
  },
  // Card processors send `invalid_account` for card numbers, so these must not read as a bank account
  invalid_account: {
    '301': 'incorrect_number', // Invalid Account Number
    '14': 'incorrect_number',
    '323': 'incorrect_number', // No such issuer
    '330': 'card_not_supported', // Invalid Payment Type
    '501': 'account_closed', // The account was closed
    '014': 'card_declined', // From Account Does Not Exist
    '015': 'card_declined',
    '021': 'card_declined',
    '055': 'card_declined', // Card Has No Customer Connected
    '52': 'card_declined', // No Checking Account
    '53': 'card_declined' // No Savings Account
  },
  transaction_not_allowed: {
    '024': 'limit_exceeded', // Daily Card Limit Exceeded
    '179': 'limit_exceeded', // Daily Check Verify Limit Exceeded
    '117': 'processing_error' // Transaction Reply Rejected / Response Timeout
  },
  processing_error: {
    '030': 'card_declined' // Card Status Abnormal
  },
  '': {
    '51': 'insufficient_funds',
    '54': 'expired_card',
    NE: 'expired_card',
    '82': 'incorrect_cvc',
    N7: 'incorrect_cvc',
    '14': 'incorrect_number',
    '15': 'incorrect_number',
    '07': 'suspected_fraud',
    '41': 'suspected_fraud',
    '43': 'suspected_fraud',
    '62': 'suspected_fraud',
    '63': 'suspected_fraud',
    F1: 'suspected_fraud',
    NC: 'suspected_fraud',
    '13': 'limit_exceeded',
    '21': 'limit_exceeded',
    '61': 'limit_exceeded',
    '65': 'limit_exceeded',
    '75': 'limit_exceeded',
    '03': 'processing_error',
    '06': 'processing_error',
    '19': 'processing_error',
    '80': 'processing_error',
    '81': 'processing_error',
    '88': 'processing_error',
    '91': 'processing_error',
    '96': 'processing_error',
    NS: 'processing_error',
    '01': 'card_declined',
    '02': 'card_declined',
    '05': 'card_declined',
    '12': 'card_declined',
    '29': 'card_declined',
    '39': 'card_declined',
    '52': 'card_declined',
    '53': 'card_declined',
    '55': 'card_declined',
    '57': 'card_declined',
    '58': 'card_declined',
    '78': 'card_declined',
    '93': 'card_declined',
    '5C': 'card_declined',
    '6P': 'card_declined',
    '9G': 'card_declined',
    N3: 'card_declined',
    N4: 'card_declined',
    ND: 'card_declined',
    NP: 'card_declined',
    NR: 'card_declined'
  }
}

// Payrix prose (`returnDescription`, `txnResults.message`) for codes we can't map, e.g. 'The account was closed'
// cspell:ignore authori unauthori
const FAILURE_MESSAGE_PATTERNS: Array<[RegExp, PaymentFailureCode]> = [
  [/insufficient funds|uncollected funds/i, 'insufficient_funds'],
  [/account (is |was |has been )?(closed|frozen)|closed account/i, 'account_closed'],
  [/suspected fraud|pick ?up card/i, 'suspected_fraud'],
  [/stop payment|payment stopped|revoked|not authori[sz]ed|unauthori[sz]ed/i, 'authorization_revoked'],
  [/no account|invalid account|unable to locate account|account number|routing number/i, 'invalid_account'],
  [/error connecting|network error|could not be processed/i, 'processing_error']
]

const fromMessage = (message?: string | null) =>
  FAILURE_MESSAGE_PATTERNS.find(([pattern]) => pattern.test(message ?? ''))?.[1]

// A NACHA or Canadian PAD return code named in prose: 'R01: Insufficient funds', 'R901: NSF', 'R900-07: …' (→ R900)
const findAchReturnCode = (text?: string | null) => text?.match(/\bR\d{2,3}\b/)?.[0]

/**
 * From a Payrix `errorCode`, or a `txnResults` row (`bankCode`, `message`, `originalCode`). ACH return rows have no
 * bankCode, only a message such as 'R01: Insufficient funds', so the R-code is read from the message.
 */
export function fromPayrixErrorCode(
  errorCode?: string | null,
  message?: string | null,
  originalCode?: string | null
): PaymentFailureReason {
  const achReturnCode = errorCode ? undefined : findAchReturnCode(message)
  return compact({
    code:
      (originalCode && PAYRIX_ORIGINAL_CODES[errorCode ?? '']?.[originalCode]) ||
      (errorCode && PAYRIX_CODES[errorCode]) ||
      (achReturnCode && ACH_RETURN_CODES[achReturnCode]) ||
      fromMessage(message) ||
      'unknown',
    providerCode: errorCode || achReturnCode || undefined,
    message: message ?? undefined
  })
}

/** From a Payrix "Returned" webhook `response.alert`; `returnDescription` is prose that may name a NACHA code (R01). */
export function fromPayrixReturn(alert?: { returnDescription?: string | null } | null): PaymentFailureReason {
  const description = alert?.returnDescription ?? undefined
  const providerCode = findAchReturnCode(description)
  const code = (providerCode && ACH_RETURN_CODES[providerCode]) || fromMessage(description) || 'unknown'
  return compact({ code, providerCode, message: description })
}

/**
 * Re-maps a stored `unknown` reason with today's tables, so a code added to a map later also fixes older records.
 * Provider codes don't collide across Payrix, Stripe and NACHA in a way that changes the meaning.
 */
export function refineUnknownReason<T extends { code: string; providerCode?: string | null; message?: string | null }>(
  reason: T
): T {
  if (reason.code !== 'unknown') return reason
  const providerCode = reason.providerCode ?? undefined
  const code =
    (providerCode && (PAYRIX_CODES[providerCode] ?? STRIPE_CODES[providerCode] ?? ACH_RETURN_CODES[providerCode])) ||
    fromMessage(reason.message)
  return code ? { ...reason, code } : reason
}

/**
 * The form stored on `Payment.failureReason`. Quote docs reach customer-view, so the raw provider message is kept
 * only when the code is `unknown`: then it is the only meaningful text the contractor can see (#20588).
 * Fraud-type codes are mapped, so their messages are never stored.
 */
// Accepts the generated GraphQL shape (string code, nullable fields) as well as PaymentFailureReason
export function toPaymentFailureReason(
  reason?: { code: string; providerCode?: string | null; message?: string | null } | null
): PaymentFailureReason | undefined {
  if (!reason?.code) return undefined
  return {
    code: reason.code as PaymentFailureCode,
    ...(reason.providerCode && { providerCode: reason.providerCode }),
    ...(reason.code === 'unknown' && reason.message && { message: reason.message })
  }
}
